import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import ParentLayout from './Layout';
import { listEnrollments } from '../api/enrollments';
import { listSessions } from '../api/sessions';

const Timetable = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState('');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const enr = await listEnrollments({ status: 'active' });
        setEnrollments(enr || []);
        const first = enr?.[0]?.studentId?._id || '';
        setSelectedLearner(first);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load learners');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadSessionsData = async () => {
      if (!selectedLearner) return;
      try {
        setLoading(true);
        const data = await listSessions({ studentId: selectedLearner });
        setSessions(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load timetable');
      } finally {
        setLoading(false);
      }
    };
    loadSessionsData();
  }, [selectedLearner]);

  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const dailySessions = useMemo(() => {
    const day = selectedDate;
    return (sessions || [])
      .filter((s) => s?.startTime && sameDay(new Date(s.startTime), day))
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  }, [sessions, selectedDate]);

  const formatDateInput = (value) => {
    if (!value) return '';
    try {
      return new Date(value).toISOString().slice(0, 10);
    } catch {
      return '';
    }
  };

  return (
    <ParentLayout>
      <Box>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" fontWeight={800} color="primary.main">
              Timetable
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View your learner's scheduled sessions and live class links.
            </Typography>
          </Box>

          {(loading) && <LinearProgress />}
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            select
            label="Select learner"
            value={selectedLearner}
            onChange={(e) => setSelectedLearner(e.target.value)}
            sx={{ maxWidth: 320 }}
          >
            {enrollments.map((enr) => (
              <MenuItem key={enr.studentId?._id} value={enr.studentId?._id}>
                {`${enr.studentId?.name || ''} ${enr.studentId?.surname || ''} (${enr.grade ? `G${enr.grade}` : ''})`}
              </MenuItem>
            ))}
          </TextField>

          <Card elevation={4}>
            <CardHeader
              title="Daily Schedule"
              subheader="Period-by-period view for a selected day"
              action={
                <TextField
                  type="date"
                  size="small"
                  value={formatDateInput(selectedDate)}
                  onChange={(e) => setSelectedDate(new Date(e.target.value))}
                  InputLabelProps={{ shrink: true }}
                />
              }
            />
            <CardContent>
              {dailySessions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No lessons scheduled for this day.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {dailySessions.map((session, idx) => {
                    const start = new Date(session.startTime);
                    const end = session.endTime
                      ? new Date(session.endTime)
                      : new Date(start.getTime() + 65 * 60 * 1000);
                    return (
                      <Grid item xs={12} md={6} key={session._id}>
                        <Card variant="outlined">
                          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip size="small" color="primary" label={`Period ${idx + 1}`} />
                              <Typography fontWeight={700}>
                                {session.subject || session.title || 'Lesson'}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} -{' '}
                              {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                            {session.joinUrl && (
                              <Button
                                variant="outlined"
                                size="small"
                                href={session.joinUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Join live
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </CardContent>
          </Card>

          <Card elevation={4}>
            <CardHeader title="Upcoming Sessions" subheader="Read-only schedule with live class links" />
            <CardContent>
              <Grid container spacing={2}>
                {sessions.map((session) => (
                  <Grid item xs={12} md={6} key={session._id}>
                    <Card variant="outlined">
                      <CardHeader title={session.title} subheader={session.subject} />
                      <CardContent>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
 {session.startTime ? new Date(session.startTime).toLocaleString() : ''}
                          </Typography>
                          {session.joinUrl && (
                            <Button
                              href={session.joinUrl}
                              target="_blank"
                              rel="noreferrer"
                              variant="outlined"
                              size="small"
                            >
                              Join live
                            </Button>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                {sessions.length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      No sessions scheduled.
                    </Typography>
                  </Grid>
                )}
              </Grid>
              <Divider sx={{ mt: 2 }} />
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </ParentLayout>
  );
};

export default Timetable;
